import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import { Popover } from '@/components/Base/Headless';
import Litepicker from '@/components/Base/Litepicker';
import {
  fetchDashboardStats,
  type DashboardContactMode,
  type DashboardStats,
} from '@/api/dashboard';
import Chart from '@/components/Base/Chart';

const DASHBOARD_DATE_RANGE_STORAGE_KEY = 'ldsurvey:admin:dashboardDateRange';
const DASHBOARD_CONTACT_MODE_STORAGE_KEY = 'ldsurvey:admin:dashboardContactMode';
const DASHBOARD_REFRESH_MS = 1 * 60 * 1000;

const chartBox =
  'box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-3 h-[420px] shadow-sm flex flex-col';
const chartTitle =
  'text-sm font-semibold text-slate-600 dark:text-slate-300 text-left mb-2';

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US');

const COLORS = [
  '#93c5fd',
  '#86efac',
  '#fcd34d',
  '#fca5a5',
  '#c4b5fd',
  '#f9a8d4',
  '#6ee7b7',
  '#fdba74',
  '#a5f3fc',
  '#d9f99d',
  '#fbcfe8',
  '#bfdbfe',
  '#bbf7d0',
  '#fef08a',
  '#fecaca',
];

const BAR_DATALABELS = {
  display: true,
  anchor: 'end' as const,
  align: 'end' as const,
  offset: 2,
  color: '#1e293b',
  font: { size: 9, weight: 600 as const },
  formatter: (value: number) => (value > 0 ? NUMBER_FORMATTER.format(value) : ''),
};

const BASE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 500 },
  plugins: {
    legend: {
      labels: { boxWidth: 10, boxHeight: 10, font: { size: 10 }, padding: 8 },
    },
    tooltip: { bodyFont: { size: 11 }, titleFont: { size: 11 }, padding: 8, cornerRadius: 8 },
  },
  scales: {
    x: {
      ticks: { font: { size: 10 }, maxRotation: 0 },
      grid: { color: 'rgba(148,163,184,0.25)', borderDash: [2, 2] as [number, number] },
      border: { display: false },
    },
    y: {
      ticks: { font: { size: 10 } },
      grid: { color: 'rgba(148,163,184,0.25)', borderDash: [2, 2] as [number, number] },
      border: { display: false },
    },
  },
};

function formatPieLabel(value: number, total: number, mode: 'percentage' | 'value') {
  if (!value) return '';
  if (mode === 'value') return NUMBER_FORMATTER.format(value);
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
  return `${pct}%`;
}

function formatLegendMetric(count: number, total: number, mode: 'percentage' | 'value'): string {
  const countText = NUMBER_FORMATTER.format(count);
  if (mode !== 'percentage') return countText;
  return `${countText}\u00A0(${formatPieLabel(count, total, 'percentage')})`;
}

function formatNumberTick(value: number | string): string | number {
  if (typeof value === 'number') return NUMBER_FORMATTER.format(value);
  return value;
}

function buildNiceAxis(values: number[], targetSteps = 5) {
  const maxValue = Math.max(0, ...values.map((v) => Number(v) || 0));
  if (maxValue <= 0) {
    return { max: 5, stepSize: 1 };
  }

  const roughStep = maxValue / targetSteps;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;

  let niceNormalized = 1;
  if (normalized > 1 && normalized <= 2) niceNormalized = 2;
  else if (normalized > 2 && normalized <= 5) niceNormalized = 5;
  else if (normalized > 5) niceNormalized = 10;

  const stepSize = niceNormalized * magnitude;
  const max = Math.ceil(maxValue / stepSize) * stepSize;

  return { max, stepSize };
}

function labelModeToggleClass(active: boolean) {
  return active
    ? 'bg-primary text-white'
    : 'text-slate-600 dark:text-slate-300';
}

function todayMYT() {
  return dayjs().format('YYYY-MM-DD');
}

function todayISORange() {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    startISO: dayjs(today).startOf('day').toISOString(),
    endISO: dayjs(today).endOf('day').toISOString(),
  };
}

function shouldAutoRefresh(range: { startISO: string; endISO: string }): boolean {
  const start = dayjs(range.startISO);
  const end = dayjs(range.endISO);
  if (!start.isValid() || !end.isValid()) return false;
  return end.diff(start, 'day') <= 31;
}

function toIsoRange(range: string) {
  const raw = (range || '').trim();
  if (!raw) return null;
  const [startRaw, endRaw] = raw.split(' - ');
  const startStr = (startRaw || '').trim();
  const endStr = ((endRaw || '') || startStr).trim();
  if (!startStr) return null;
  const start = dayjs(startStr);
  const end = dayjs(endStr);
  if (!start.isValid() || !end.isValid()) return null;
  return {
    startISO: start.startOf('day').toISOString(),
    endISO: end.endOf('day').toISOString(),
  };
}

function SurveyCompletionRateInfo() {
  return (
    <Popover className="inline-flex">
      <Popover.Button
        as="button"
        type="button"
        className="inline-flex items-center justify-center rounded-full text-slate-400 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        aria-label="How survey completion rate is calculated"
      >
        <Lucide icon="Info" className="w-3.5 h-3.5 stroke-[2]" />
      </Popover.Button>
      <Popover.Panel placement="bottom-end" className="w-64 p-3 text-left normal-case">
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
          <span className="font-medium">Total Survey Submissions</span> ÷{' '}
          <span className="font-medium">Total QR Scans</span> × 100
        </p>
        <ul className="mt-2 space-y-1 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
          <li>Total Survey Submissions — completed surveys in the selected date range</li>
          <li>Total QR Scans — times the survey was opened via ambassador QR links</li>
        </ul>
      </Popover.Panel>
    </Popover>
  );
}

const infoButtonClass =
  'inline-flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30';

function UniqueConsumerInfo({ tone = 'default' }: { tone?: 'default' | 'toolbar' }) {
  const buttonClass =
    tone === 'toolbar'
      ? `${infoButtonClass} text-white/60 hover:text-white`
      : `${infoButtonClass} text-slate-400 hover:text-primary`;

  return (
    <Popover className="inline-flex">
      <Popover.Button
        as="button"
        type="button"
        className={buttonClass}
        aria-label="How unique consumer is calculated"
      >
        <Lucide icon="Info" className="w-3.5 h-3.5 stroke-[2]" />
      </Popover.Button>
      <Popover.Panel placement="bottom-end" className="w-72 p-3 text-left normal-case">
        <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Unique Consumer</p>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
          Distinct phone numbers with at least 1 completed survey.
        </p>
      </Popover.Panel>
    </Popover>
  );
}

function ActiveConsumerInfo({ tone = 'default' }: { tone?: 'default' | 'toolbar' }) {
  const buttonClass =
    tone === 'toolbar'
      ? `${infoButtonClass} text-white/60 hover:text-white`
      : `${infoButtonClass} text-slate-400 hover:text-primary`;

  return (
    <Popover className="inline-flex">
      <Popover.Button
        as="button"
        type="button"
        className={buttonClass}
        aria-label="How active consumer is calculated"
      >
        <Lucide icon="Info" className="w-3.5 h-3.5 stroke-[2]" />
      </Popover.Button>
      <Popover.Panel placement="bottom-end" className="w-72 p-3 text-left normal-case">
        <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Active Consumer</p>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
          Distinct phone numbers with 2 or more completed surveys.
        </p>
      </Popover.Panel>
    </Popover>
  );
}

function Main() {
  const readInitialDateRange = () => {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(DASHBOARD_DATE_RANGE_STORAGE_KEY);
    const value = (raw || '').trim();
    return value.length > 0 ? value : null;
  };

  const readInitialContactMode = (): DashboardContactMode => {
    if (typeof window === 'undefined') return 'unique';
    const raw = window.localStorage.getItem(DASHBOARD_CONTACT_MODE_STORAGE_KEY);
    if (raw === 'both') return 'both';
    return raw === 'active' ? 'active' : 'unique';
  };

  const todayStr = todayMYT();
  const initialSavedRange = readInitialDateRange();
  const [dateRange, setDateRange] = useState(initialSavedRange ?? `${todayStr} - ${todayStr}`);
  const [contactMode, setContactMode] = useState<DashboardContactMode>(readInitialContactMode);
  const [ethnicLabelMode, setEthnicLabelMode] = useState<'percentage' | 'value'>('percentage');
  const [ageLabelMode, setAgeLabelMode] = useState<'percentage' | 'value'>('value');
  const [confirmedRange, setConfirmedRange] = useState<{ startISO: string; endISO: string } | null>(
    (initialSavedRange && toIsoRange(initialSavedRange)) || todayISORange()
  );
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const dashboardExportRef = useRef<HTMLDivElement | null>(null);

  const dateRangeISO = useMemo(() => toIsoRange(dateRange), [dateRange]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const value = (dateRange || '').trim();
    if (!value) return;
    window.localStorage.setItem(DASHBOARD_DATE_RANGE_STORAGE_KEY, value);
  }, [dateRange]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(DASHBOARD_CONTACT_MODE_STORAGE_KEY, contactMode);
  }, [contactMode]);

  const isUniqueSelected = contactMode === 'unique' || contactMode === 'both';
  const isActiveSelected = contactMode === 'active' || contactMode === 'both';

  const handleUniqueToggle = (checked: boolean) => {
    if (checked) {
      setContactMode(isActiveSelected ? 'both' : 'unique');
      return;
    }
    if (isActiveSelected) setContactMode('active');
  };

  const handleActiveToggle = (checked: boolean) => {
    if (checked) {
      setContactMode(isUniqueSelected ? 'both' : 'active');
      return;
    }
    if (isUniqueSelected) setContactMode('unique');
  };

  const handleSearch = () => {
    if (!dateRangeISO) return;
    setConfirmedRange({ ...dateRangeISO });
  };

  const fetchDashboard = useCallback(
    async (
      range: { startISO: string; endISO: string },
      options?: { background?: boolean }
    ) => {
      const background = options?.background ?? false;
      if (background && abortRef.current) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setError(null);
      if (background) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const data = await fetchDashboardStats({
          startISO: range.startISO,
          endISO: range.endISO,
          contactMode,
        });
        if (controller.signal.aborted) return;
        setStats(data);
        setLastUpdatedAt(new Date());
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        const isActiveRequest = abortRef.current === controller;
        if (isActiveRequest) {
          abortRef.current = null;
        }
        if (background && isActiveRequest) {
          setIsRefreshing(false);
        } else if (!background && isActiveRequest) {
          setLoading(false);
        }
      }
    },
    [contactMode]
  );

  useEffect(() => {
    if (!confirmedRange) return;
    void fetchDashboard(confirmedRange);
  }, [confirmedRange, fetchDashboard, contactMode]);

  useEffect(() => {
    if (!confirmedRange) return;
    if (!shouldAutoRefresh(confirmedRange)) return;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void fetchDashboard(confirmedRange, { background: true });
    }, DASHBOARD_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [confirmedRange, fetchDashboard]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const handleExportToPdf = useCallback(async () => {
    if (!dashboardExportRef.current || loading || isExportingPdf || !stats) return;

    setIsExportingPdf(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 5;
      const marginY = 10;
      const printableWidth = pageWidth - marginX * 2;
      const printableHeight = pageHeight - marginY * 2;
      const exportRangeStart = confirmedRange ? dayjs(confirmedRange.startISO) : null;
      const exportRangeEnd = confirmedRange ? dayjs(confirmedRange.endISO) : null;
      const rangeLabel =
        exportRangeStart && exportRangeEnd
          ? `${exportRangeStart.format('DD MMM YYYY')} - ${exportRangeEnd.format('DD MMM YYYY')}`
          : (dateRange || '').trim();

      const captureTarget = dashboardExportRef.current;
      const exportBlocks = Array.from(
        captureTarget.querySelectorAll<HTMLElement>('[data-pdf-block="true"]')
      ).filter((element) => element.offsetParent !== null);

      if (exportBlocks.length === 0) {
        throw new Error('No visible dashboard sections to export');
      }

      pdf.setFontSize(12);
      pdf.setTextColor(30, 41, 59);
      pdf.text(`Date Range: ${rangeLabel}`, marginX, marginY);

      let cursorY = marginY + 8;
      const sectionGap = 6;

      for (let i = 0; i < exportBlocks.length; i += 1) {
        const block = exportBlocks[i];
        const rect = block.getBoundingClientRect();
        if (rect.width < 2 || rect.height < 2) continue;

        const canvas = await html2canvas(block, {
          scale: 1.35,
          useCORS: true,
          backgroundColor: '#ffffff',
          width: Math.ceil(rect.width),
          height: Math.ceil(rect.height),
          scrollX: -window.scrollX,
          scrollY: -window.scrollY,
          logging: false,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        const baseWidth = printableWidth;
        const baseHeight = (canvas.height * baseWidth) / canvas.width;
        const scaleToFitHeight = baseHeight > printableHeight ? printableHeight / baseHeight : 1;
        const renderWidth = baseWidth * scaleToFitHeight;
        const renderHeight = baseHeight * scaleToFitHeight;

        if (i > 0 && cursorY + renderHeight > pageHeight - marginY) {
          pdf.addPage();
          cursorY = marginY;
        }

        const x = marginX + (printableWidth - renderWidth) / 2;
        pdf.addImage(imgData, 'JPEG', x, cursorY, renderWidth, renderHeight, undefined, 'FAST');
        cursorY += renderHeight + sectionGap;
      }

      const safeRange = (dateRange || '')
        .replace(/\s+/g, '_')
        .replace(/[^0-9_-]/g, '')
        .slice(0, 40);
      const fallbackDate = dayjs().format('YYYYMMDD');
      pdf.save(`dashboard_${safeRange || fallbackDate}_${contactMode}.pdf`);
    } catch (err: unknown) {
      setError(err instanceof Error ? `Failed to export PDF: ${err.message}` : 'Failed to export PDF');
    } finally {
      setIsExportingPdf(false);
    }
  }, [confirmedRange, contactMode, dateRange, isExportingPdf, loading, stats]);

  const showLoadingState = loading && !stats;
  const contactMetricLabel =
    contactMode === 'both'
      ? 'Total Survey Submission:'
      : contactMode === 'active'
        ? 'Active Consumer Collected:'
        : 'Unique Consumer Collected:';
  const contactMetricValue =
    contactMode === 'both'
      ? (stats?.totalCustomers ?? 0)
      : contactMode === 'active'
        ? (stats?.activeContactCount ?? 0)
        : (stats?.uniqueContactCount ?? 0);

  const submissionsByMonthChart = useMemo(() => {
    const rows = stats?.submissionsByMonth ?? [];
    const labels = rows.map((row) => row.month);
    const datasets = [];

    if (isUniqueSelected) {
      datasets.push({
        label: 'Unique Consumer',
        data: rows.map((row) => row.uniqueCount),
        backgroundColor: '#2563eb',
        borderRadius: 4,
        barThickness: 12,
      });
    }

    if (isActiveSelected) {
      datasets.push({
        label: 'Active Consumer',
        data: rows.map((row) => row.activeCount),
        backgroundColor: '#f59e0b',
        borderRadius: 4,
        barThickness: 12,
      });
    }

    return {
      type: 'bar' as const,
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 8 },
        },
        plugins: {
          legend: {
            display: datasets.length > 1,
            position: 'bottom' as const,
          },
          datalabels: {
            anchor: 'end' as const,
            align: 'top' as const,
            color: '#64748b',
            font: { size: 10 },
            clip: false,
            offset: 2,
          },
        },
        datasets: {
          bar: {
            barPercentage: 1,
            categoryPercentage: datasets.length > 1 ? 0.55 : 0.35,
          },
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            beginAtZero: true,
            grace: '15%',
            ticks: { precision: 0 },
          },
        },
      },
    };
  }, [stats?.submissionsByMonth, isUniqueSelected, isActiveSelected]);

  const ethnicBreakdownData = useMemo(() => {
    const rows = stats?.ethnicBreakdown ?? [];
    const labels = rows.map((row) => row.race);
    const data = rows.map((row) => row.count);
    const colors = labels.map((_, i) => COLORS[i % COLORS.length]);
    return {
      labels,
      legend: labels.map((label, i) => ({ label, count: data[i], color: colors[i] })),
      datasets: [
        {
          label: 'Count',
          data,
          backgroundColor: colors,
          borderColor: colors,
          borderWidth: 1,
        },
      ],
    };
  }, [stats?.ethnicBreakdown]);

  const ethnicBreakdownOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 500 },
      plugins: {
        legend: { display: false },
        datalabels: {
          display: true,
          color: '#1e293b',
          font: { size: 10, weight: 700 as const },
          formatter: (value: number, ctx: { dataset: { data: number[] } }) => {
            if (!value) return '';
            const total = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0);
            return formatPieLabel(value, total, ethnicLabelMode);
          },
        },
        tooltip: {
          bodyFont: { size: 11 },
          titleFont: { size: 11 },
          padding: 8,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: { label?: string; parsed: number; dataset: { data: number[] } }) => {
              const total = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0);
              const formatted = formatPieLabel(ctx.parsed, total, ethnicLabelMode);
              return ` ${ctx.label}: ${formatted}`;
            },
          },
        },
      },
    }),
    [ethnicLabelMode]
  );

  const ageGroupData = useMemo(() => {
    const rows = stats?.ageGroupDistribution ?? [];
    return {
      labels: rows.map((row) => row.age),
      datasets: [
        {
          label: 'Count',
          data: rows.map((row) => row.count),
          backgroundColor: '#93c5fd',
          borderColor: '#60a5fa',
          borderWidth: 1,
          borderRadius: 5,
          borderSkipped: false,
          barThickness: 20,
          maxBarThickness: 24,
        },
      ],
    };
  }, [stats?.ageGroupDistribution]);

  const ageAxis = useMemo(
    () => buildNiceAxis((stats?.ageGroupDistribution ?? []).map((row) => row.count)),
    [stats?.ageGroupDistribution]
  );

  const ageGroupOptions = useMemo(
    () => ({
      ...BASE_OPTIONS,
      plugins: {
        ...BASE_OPTIONS.plugins,
        legend: { display: false },
        datalabels: {
          ...BAR_DATALABELS,
          formatter: (value: number, ctx: { dataset: { data: number[] } }) => {
            if (!value) return '';
            if (ageLabelMode === 'value') return NUMBER_FORMATTER.format(value);
            const total = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0);
            return formatPieLabel(value, total, 'percentage');
          },
        },
        tooltip: {
          bodyFont: { size: 11 },
          titleFont: { size: 11 },
          padding: 8,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: { label?: string; parsed?: { y?: number }; dataset: { data: number[] } }) => {
              const value = ctx.parsed?.y ?? 0;
              if (ageLabelMode === 'value') return ` ${ctx.label}: ${NUMBER_FORMATTER.format(value)}`;
              const total = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0);
              return ` ${ctx.label}: ${formatPieLabel(value, total, 'percentage')}`;
            },
          },
        },
      },
      scales: {
        ...BASE_OPTIONS.scales,
        y: {
          ...BASE_OPTIONS.scales.y,
          beginAtZero: true,
          grace: '15%',
          ticks: {
            ...BASE_OPTIONS.scales.y.ticks,
            stepSize: ageAxis.stepSize,
            callback: formatNumberTick,
          },
        },
      },
    }),
    [ageAxis.stepSize, ageLabelMode]
  );

  const geographicByStateData = useMemo(() => {
    const rows = stats?.geographicByState ?? [];
    return {
      labels: rows.map((row) => row.state),
      datasets: [
        {
          label: 'Count',
          data: rows.map((row) => row.count),
          backgroundColor: '#93c5fd',
          borderColor: '#60a5fa',
          borderWidth: 1,
          borderRadius: 5,
          borderSkipped: false,
          barThickness: 20,
          maxBarThickness: 24,
        },
      ],
    };
  }, [stats?.geographicByState]);

  const geographicByStateAxis = useMemo(
    () => buildNiceAxis((geographicByStateData.datasets?.[0]?.data as number[]) ?? []),
    [geographicByStateData]
  );

  const geographicByStateOptions = useMemo(
    () => ({
      ...BASE_OPTIONS,
      plugins: {
        ...BASE_OPTIONS.plugins,
        legend: { display: false },
        datalabels: { ...BAR_DATALABELS },
      },
      scales: {
        ...BASE_OPTIONS.scales,
        x: {
          ...BASE_OPTIONS.scales.x,
          ticks: { ...BASE_OPTIONS.scales.x.ticks, maxRotation: 45, minRotation: 30 },
        },
        y: {
          ...BASE_OPTIONS.scales.y,
          beginAtZero: true,
          grace: '15%',
          ticks: {
            ...BASE_OPTIONS.scales.y.ticks,
            stepSize: geographicByStateAxis.stepSize,
            callback: formatNumberTick,
          },
        },
      },
    }),
    [geographicByStateAxis.stepSize]
  );

  const ethnicBreakdownTotal = useMemo(
    () => ethnicBreakdownData.datasets[0]?.data.reduce((a, b) => a + b, 0) ?? 0,
    [ethnicBreakdownData]
  );

  return (
    <div className="p-6">
      <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative inline-flex">
            <Lucide
              icon="Calendar"
              className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
            />
            <Litepicker
              value={dateRange}
              onChange={(e) => setDateRange((e.target?.value || '').trim())}
              options={{
                autoApply: true,
                singleMode: false,
                numberOfColumns: 2,
                numberOfMonths: 2,
                showWeekNumbers: true,
                dropdowns: { minYear: 2024, maxYear: null, months: true, years: true },
                format: 'YYYY-MM-DD',
              }}
              className="w-[270px] pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-sm"
            />
          </div>
          <div className="inline-flex items-center gap-3 rounded-[0.5rem] px-3 py-2 border border-white/20 bg-white/[0.08] text-xs text-white/95">
            <div className="inline-flex items-center gap-1">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  name="contactMode"
                  className="h-3.5 w-3.5"
                  checked={isUniqueSelected}
                  onChange={(e) => handleUniqueToggle(e.target.checked)}
                />
                <span>Unique Consumer</span>
              </label>
              <UniqueConsumerInfo tone="toolbar" />
            </div>
            <div className="inline-flex items-center gap-1">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  name="contactMode"
                  className="h-3.5 w-3.5"
                  checked={isActiveSelected}
                  onChange={(e) => handleActiveToggle(e.target.checked)}
                />
                <span>Active Consumer</span>
              </label>
              <ActiveConsumerInfo tone="toolbar" />
            </div>
          </div>
          <Button
            variant="primary"
            onClick={handleSearch}
            disabled={!dateRangeISO || loading}
            className="w-full sm:w-auto"
          >
            {loading ? (
              <>
                <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                Loading…
              </>
            ) : (
              <>
                <Lucide icon="Search" className="stroke-[1.3] w-4 h-4 mr-2" />
                Search
              </>
            )}
          </Button>
          <Button
            variant="primary"
            onClick={handleExportToPdf}
            disabled={loading || isExportingPdf || !stats}
            className="w-full sm:w-auto font-semibold shadow-md"
          >
            {isExportingPdf ? (
              <>
                <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                Exporting…
              </>
            ) : (
              <>
                <Lucide icon="FileDown" className="stroke-[1.3] w-4 h-4 mr-2" />
                Export to PDF
              </>
            )}
          </Button>
        </div>
        <div className="inline-flex items-center text-xs text-white/95">
          {isRefreshing ? (
            <>
              <Lucide icon="Loader" className="stroke-[1.3] w-3.5 h-3.5 mr-2 animate-spin" />
              Refreshing dashboard...
            </>
          ) : lastUpdatedAt ? (
            <>
              <Lucide icon="Clock3" className="stroke-[1.3] w-3.5 h-3.5 mr-2" />
              Last updated at {dayjs(lastUpdatedAt).format('HH:mm:ss')}
            </>
          ) : null}
        </div>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
          {error}
        </div>
      )}

      <div ref={dashboardExportRef} className="space-y-5">
        <div className="mb-5" data-pdf-block="true">
          <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm flex items-center gap-0 divide-x divide-slate-200 dark:divide-darkmode-400">
            <div className="flex flex-1 items-center justify-center gap-3 px-6">
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                {contactMetricLabel}
              </p>
              <p className="text-3xl font-bold tabular-nums text-slate-800 dark:text-slate-100 flex-shrink-0">
                {showLoadingState ? (
                  <span className="text-xl text-slate-400 animate-pulse">…</span>
                ) : (
                  contactMetricValue.toLocaleString()
                )}
              </p>
            </div>
            <div className="flex flex-1 items-center justify-center gap-3 px-6">
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  Survey Completion Rate:
                </p>
                <SurveyCompletionRateInfo />
              </div>
              <p className="text-3xl font-bold tabular-nums text-slate-800 dark:text-slate-100 flex-shrink-0">
                {showLoadingState ? (
                  <span className="text-xl text-slate-400 animate-pulse">…</span>
                ) : (
                  `${stats?.surveyCompletionRate ?? 0}%`
                )}
              </p>
            </div>
            <div className="flex flex-1 items-center justify-center pl-6">
              <div className="inline-flex flex-col items-start gap-1">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    TnG Redemption Count:
                  </p>
                  {showLoadingState ? (
                    <span className="text-xl text-slate-400 animate-pulse flex-shrink-0">…</span>
                  ) : (
                    <div className="flex items-baseline gap-2 flex-shrink-0 whitespace-nowrap">
                      <span className="text-3xl font-bold tabular-nums text-slate-800 dark:text-slate-100">
                        {(stats?.tngUsed ?? 0).toLocaleString()}
                      </span>
                      <span className="text-lg font-semibold text-slate-400 dark:text-slate-500">
                        / {(stats?.tngTotal ?? 0).toLocaleString()}
                      </span>
                      <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                        ({stats?.tngPct ?? 0}% Used)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5">
          <div className={chartBox} data-pdf-block="true">
            <p className={chartTitle}>Submissions by Month</p>
            <div className="flex-1 min-h-0">
              {showLoadingState ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  <Lucide icon="Loader" className="w-5 h-5 mr-2 animate-spin" />
                  Loading chart…
                </div>
              ) : (stats?.submissionsByMonth?.length ?? 0) === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  No submission data for the selected date range
                </div>
              ) : (
                <Chart {...submissionsByMonthChart} height="auto" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <div className={chartBox} data-pdf-block="true">
              <div className="flex items-center justify-between mb-2">
                <p className={`${chartTitle} mb-0`}>Consumer Ethnic Breakdown</p>
                <div className="inline-flex items-center gap-1 rounded-md border border-slate-300/70 dark:border-darkmode-400 p-1 text-xs">
                  <button
                    type="button"
                    onClick={() => setEthnicLabelMode('percentage')}
                    className={`px-2 py-1 rounded ${labelModeToggleClass(ethnicLabelMode === 'percentage')}`}
                  >
                    %
                  </button>
                  <button
                    type="button"
                    onClick={() => setEthnicLabelMode('value')}
                    className={`px-2 py-1 rounded ${labelModeToggleClass(ethnicLabelMode === 'value')}`}
                  >
                    Value
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0">
                {showLoadingState ? (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    <Lucide icon="Loader" className="w-5 h-5 mr-2 animate-spin" />
                    Loading chart…
                  </div>
                ) : ethnicBreakdownTotal === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    No ethnic breakdown data for the selected date range
                  </div>
                ) : (
                  <div className="h-full flex items-center">
                    <div className="w-[72%] h-full flex items-center justify-center">
                      <div className="w-[280px] h-[280px] max-w-full">
                        <Chart type="pie" data={ethnicBreakdownData} options={ethnicBreakdownOptions} />
                      </div>
                    </div>
                    <div className="w-[28%] flex flex-col justify-center gap-1.5 pl-2 overflow-y-auto max-h-full flex-shrink-0">
                      {ethnicBreakdownData.legend.map((item) => (
                        <div key={`ethnic-${item.label}`} className="flex items-center gap-1 min-w-0">
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: item.color }}
                          />
                          <span className="text-[11px] text-slate-600 dark:text-slate-400 leading-[1.65]">
                            {item.label}:{' '}
                            {formatLegendMetric(item.count, ethnicBreakdownTotal, ethnicLabelMode)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={chartBox} data-pdf-block="true">
              <div className="flex items-center justify-between mb-2">
                <p className={`${chartTitle} mb-0`}>Age Group Distribution</p>
                <div className="inline-flex items-center gap-1 rounded-md border border-slate-300/70 dark:border-darkmode-400 p-1 text-xs">
                  <button
                    type="button"
                    onClick={() => setAgeLabelMode('percentage')}
                    className={`px-2 py-1 rounded ${labelModeToggleClass(ageLabelMode === 'percentage')}`}
                  >
                    %
                  </button>
                  <button
                    type="button"
                    onClick={() => setAgeLabelMode('value')}
                    className={`px-2 py-1 rounded ${labelModeToggleClass(ageLabelMode === 'value')}`}
                  >
                    Value
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0">
                {showLoadingState ? (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    <Lucide icon="Loader" className="w-5 h-5 mr-2 animate-spin" />
                    Loading chart…
                  </div>
                ) : (stats?.ageGroupDistribution?.length ?? 0) === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    No age group data for the selected date range
                  </div>
                ) : (
                  <Chart type="bar" data={ageGroupData} options={ageGroupOptions} height="auto" />
                )}
              </div>
            </div>
          </div>

          <div className={chartBox} data-pdf-block="true">
            <p className={chartTitle}>Geographic Distribution by State</p>
            <div className="flex-1 min-h-0">
              {showLoadingState ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  <Lucide icon="Loader" className="w-5 h-5 mr-2 animate-spin" />
                  Loading chart…
                </div>
              ) : (stats?.geographicByState?.length ?? 0) === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  No state distribution data for the selected date range
                </div>
              ) : (
                <Chart
                  type="bar"
                  data={geographicByStateData}
                  options={geographicByStateOptions}
                  height="auto"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Main;
